// File: themes/memoir/assets/js/components/Testimonials.jsx
import React, { useState, useEffect } from 'react';
import { Search, Filter, User, ChevronLeft, ChevronRight, Loader2, Tag } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

const ITEMS_PER_PAGE = 6;

const TestimonialsPage = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRelationship, setSelectedRelationship] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [sortOrder, setSortOrder] = useState('newest');

    useEffect(() => {
        const fetchTestimonials = async () => {
            try {
                // This would be replaced with actual API call
                const mockTestimonials = [
                    {
                        id: 1,
                        author: "[TEST] Laurence Avent",
                        relationship: "Friend",
                        date: "2024-11-20",
                        content: "Gareth Shellard was the finest man I've ever known...",
                        tags: ["climbing", "friendship"]
                    },
                    {
                        id: 2,
                        author: "[TEST] ",
                        relationship: "[TEST] Climbing Partner",
                        date: "2024-11-19",
                        content: "I had the privilege of sharing many climbs with Gareth...",
                        tags: ["mountaineering", "friendship"]
                    }
                ];
                setTestimonials(mockTestimonials);
                setLoading(false);
            } catch (err) {
                setError("Failed to load testimonials");
                setLoading(false);
            }
        };

        fetchTestimonials();
    }, []);

    const relationships = [...new Set(testimonials.map(t => t.relationship))];

    // Filter and sort testimonials
    const filteredTestimonials = testimonials
        .filter(testimonial => {
            const matchesRelationship = selectedRelationship === 'all' ||
                testimonial.relationship === selectedRelationship;
            const matchesSearch = testimonial.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                testimonial.author.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesRelationship && matchesSearch;
        })
        .sort((a, b) => {
            if (sortOrder === 'newest') {
                return new Date(b.date) - new Date(a.date);
            }
            return new Date(a.date) - new Date(b.date);
        });

    // Calculate pagination
    const totalPages = Math.ceil(filteredTestimonials.length / ITEMS_PER_PAGE);
    const paginatedTestimonials = filteredTestimonials.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const PaginationControls = () => (
        <div className="flex items-center justify-between mt-8">
            <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50"
            >
                <ChevronLeft className="w-4 h-4" />
                Previous
            </button>

            <div className="flex gap-2">
                {[...Array(totalPages)].map((_, i) => (
                    <button
                        key={i}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`w-8 h-8 rounded-full ${
                            currentPage === i + 1
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                    >
                        {i + 1}
                    </button>
                ))}
            </div>

            <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50"
            >
                Next
                <ChevronRight className="w-4 h-4" />
            </button>
        </div>
    );

    // Loading state
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="space-y-8">
            <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
                <div className="flex flex-col md:flex-row gap-6">
                    <div className="relative flex-grow">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                            type="text"
                            placeholder="Search testimonials..."
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>

                    <div className="flex gap-4">
                        <div className="flex items-center gap-2">
                            <Filter className="text-gray-400 h-4 w-4" />
                            <select
                                value={selectedRelationship}
                                onChange={(e) => {
                                    setSelectedRelationship(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            >
                                <option value="all">All Relationships</option>
                                {relationships.map(rel => (
                                    <option key={rel} value={rel}>{rel}</option>
                                ))}
                            </select>
                        </div>

                        <select
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                        </select>
                    </div>
                </div>

                {/* Results count */}
                <div className="text-sm text-gray-600">
                    Showing {paginatedTestimonials.length} of {filteredTestimonials.length} testimonials
                </div>
            </div>

            {/* No results state */}
            {filteredTestimonials.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                    <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900">No testimonials found</h3>
                    <p className="text-gray-600 mt-2">
                        Try adjusting your search or filters to find what you're looking for.
                    </p>
                </div>
            ) : (
                /* Testimonials Grid */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {paginatedTestimonials.map(testimonial => (
                        <a
                            href={`/testimonials/${testimonial.id}`}
                            key={testimonial.id}
                            className="block bg-white rounded-lg shadow-sm p-6 space-y-4 hover:shadow-md transition-shadow"
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    <h3 className="text-xl font-medium text-primary">{testimonial.author}</h3>
                                    <p className="text-gray-600">{testimonial.relationship}</p>
                                </div>
                                <span className="text-sm text-gray-500">
                {new Date(testimonial.date).toLocaleDateString()}
            </span>
                            </div>

                            <div className="prose max-w-none">
                                <p className="text-gray-700 line-clamp-4">{testimonial.content}</p>
                            </div>

                            {testimonial.tags?.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {testimonial.tags.map(tag => (
                                        <span
                                            key={tag}
                                            className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                                        >
                        <Tag className="w-3 h-3" />
                                            {tag.trim()}
                    </span>
                                    ))}
                                </div>
                            )}

                            <div className="text-primary hover:text-primary-dark font-medium">
                                Read full memory →
                            </div>
                        </a>
                    ))}
                </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && <PaginationControls />}
        </div>
    );
};

export default TestimonialsPage;